import { type Action, createStore } from 'react-sweet-state';
import { cloneDeep } from 'lodash';

import { type BrandBrief, brandBriefTypeHasTitle, sortBrandBriefs } from 'src/data/brief';
import {
  createBrandBrief,
  deleteBrandBrief,
  queryBrandBriefs,
  updateBrandBrief,
} from 'src/graphql/brandBrief';
import type { ID } from 'src/lib/collection';
import type { CommonContainerProps } from 'src/lib/sweetState';

export interface State {
  brandBriefs: BrandBrief[];
  loadingBrandBriefs: boolean;
  brandBriefsLoaded: boolean;
  savingBrandBrief: boolean;
  deletingBrandBrief: boolean;
}
export type Actions = typeof ACTIONS;
export type ContainerProps = CommonContainerProps<State>;

const INITIAL_STATE: State = {
  brandBriefs: [],
  loadingBrandBriefs: false,
  brandBriefsLoaded: false,
  savingBrandBrief: false,
  deletingBrandBrief: false,
};

const PRIVATE_ACTIONS = {
  clearStore:
    (): Action<State> =>
    ({ setState }) => {
      setState(cloneDeep(INITIAL_STATE));
    },

  mergeBrandBriefInCache:
    (item: BrandBrief): Action<State> =>
    ({ getState, setState }) => {
      const { brandBriefs: list } = getState();
      const itemExists = list.some((t) => t.id === item.id);
      const newList = itemExists ? list.map((t) => (t.id === item.id ? item : t)) : [...list, item];
      setState({ brandBriefs: sortBrandBriefs(newList) });
    },

  removeBrandBriefFromCache:
    (itemId: ID): Action<State> =>
    ({ getState, setState }) => {
      const { brandBriefs } = getState();
      const filteredList = brandBriefs.filter((t) => t.id !== itemId);
      setState({ brandBriefs: filteredList });
    },
};

/**
 * Public actions
 */
const ACTIONS = {
  init:
    (): Action<State> =>
    ({ dispatch }) => {
      dispatch(PRIVATE_ACTIONS.clearStore());
    },

  queryBrandBriefs:
    (brandVoiceId: ID): Action<State> =>
    async ({ setState }) => {
      setState({ loadingBrandBriefs: true });
      try {
        const result = await queryBrandBriefs(brandVoiceId);
        setState({
          brandBriefs: sortBrandBriefs(result || []),
          brandBriefsLoaded: true,
        });
      } finally {
        setState({ loadingBrandBriefs: false });
      }
    },

  createBrandBrief:
    (
      item: BrandBrief,
      brandVoiceId: ID
    ): Action<State, ContainerProps, Promise<BrandBrief | undefined>> =>
    async ({ setState, dispatch }) => {
      setState({ savingBrandBrief: true });
      try {
        const createdBrandBrief = await createBrandBrief({
          brandVoiceId,
          ...item,
        });
        if (!createdBrandBrief) {
          return undefined;
        }

        dispatch(PRIVATE_ACTIONS.mergeBrandBriefInCache(createdBrandBrief));
        return createdBrandBrief;
      } finally {
        setState({ savingBrandBrief: false });
      }
    },

  updateBrandBrief:
    (item: BrandBrief): Action<State, ContainerProps, Promise<BrandBrief | undefined>> =>
    async ({ setState, dispatch }) => {
      setState({ savingBrandBrief: true });

      if (!brandBriefTypeHasTitle(item.briefType)) {
        // if brand brief type was updated from the one that has title
        item.title = '';
      }
      try {
        const updatedBrandBrief = await updateBrandBrief({ ...item });
        if (!updatedBrandBrief) {
          return undefined;
        }

        dispatch(PRIVATE_ACTIONS.mergeBrandBriefInCache(updatedBrandBrief));
        return updatedBrandBrief;
      } finally {
        setState({ savingBrandBrief: false });
      }
    },

  deleteBrandBrief:
    (id: ID): Action<State> =>
    async ({ setState, dispatch }) => {
      setState({ deletingBrandBrief: true });
      try {
        await deleteBrandBrief(id);
        dispatch(PRIVATE_ACTIONS.removeBrandBriefFromCache(id));
      } finally {
        setState({ deletingBrandBrief: false });
      }
    },

  isSavingBrandBrief:
    (): Action<State, ContainerProps, boolean> =>
    ({ getState }) => {
      return getState().savingBrandBrief;
    },
};

export const BrandBriefsStore = createStore<State, Actions>({
  initialState: INITIAL_STATE,
  actions: ACTIONS,
  name: 'brand-briefs-store',
});
